import { Component } from '@theme/component';

/**
 * Tabs of `sections/about-tabs.liquid`. A click on a tab shows its panel; arrow keys, Home and End move between
 * tabs (WAI-ARIA tabs pattern with automatic activation). In the theme editor, selecting a tab block shows it.
 *
 * @typedef {{ tabs: HTMLButtonElement[], panels: HTMLElement[] }} Refs
 * @extends {Component<Refs>}
 */
class AboutTabsComponent extends Component {
  requiredRefs = ['tabs', 'panels'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /**
   * Bound via `on:click` on each tab.
   * @param {Event} event
   */
  select(event) {
    if (event.target instanceof HTMLButtonElement) this.#activate(event.target);
  }

  /**
   * Bound via `on:keydown` on each tab.
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const { tabs } = this.refs;
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (index === -1) return;

    /** @type {Record<string, number>} */
    const targets = {
      ArrowRight: (index + 1) % tabs.length,
      ArrowLeft: (index - 1 + tabs.length) % tabs.length,
      Home: 0,
      End: tabs.length - 1,
    };
    const next = targets[event.key];
    if (next === undefined) return;

    event.preventDefault();
    const tab = tabs[next];
    if (!tab) return;

    this.#activate(tab);
    tab.focus();
  }

  /** @param {HTMLButtonElement} tab */
  #activate(tab) {
    const { tabs, panels } = this.refs;

    for (const other of tabs) {
      const active = other === tab;
      other.setAttribute('aria-selected', String(active));
      other.tabIndex = active ? 0 : -1;
    }

    const panelId = tab.getAttribute('aria-controls');
    for (const panel of panels) {
      panel.hidden = panel.id !== panelId;
    }
  }

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    if (!(event.target instanceof HTMLButtonElement) || !this.refs.tabs.includes(event.target)) return;
    this.#activate(event.target);
  };
}

if (!customElements.get('about-tabs-component')) {
  customElements.define('about-tabs-component', AboutTabsComponent);
}
